import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	ArrowRightCircle,
	CalendarDays,
	IdCard,
	Lock,
	Mail,
	Phone,
	UserRound,
} from 'lucide-react';
import { Button, Input, InputError, InputWrapperAuth } from '@/components/ui';
import { Header } from '@/pages/public/landing/components/Header';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useResumeSchedulingPayAfterAuth } from '@/hooks/useResumeSchedulingPayAfterAuth';
import { useCreateUser } from '@/services';
import { setUserData } from '@/utils/cookies';
import { toast } from 'react-toastify';
import { useModal } from '@/hooks/useModal';
import CollaboratorTermsOfUsePage from '@/pages/collaborator-terms-of-use';
import PrivacyPolicyPage from '@/pages/public/privacy-policy';
import {
	checkBenefitRedirection,
	clearPendingBenefitData,
	markUserFirstAccess,
	maskCPF,
	maskDate,
	maskPhone,
	clearSchedulingPayDraftSilently,
	normalizeName,
	removeMask,
	schedulingSelectedDateStillBookable,
	SCHEDULING_REGISTRATION_PENDING_KEY,
	transformBirthday,
} from '@/utils';

const formatCompanyName = (rawCompanyName: string | null) => {
	if (!rawCompanyName) return null;

	try {
		const normalized = decodeURIComponent(rawCompanyName).trim();

		if (!normalized) return null;

		return normalized
			.replace(/[-_]+/g, ' ')
			.replace(/\s+/g, ' ')
			.replace(/\b\w/g, (letter) => letter.toUpperCase());
	} catch {
		const fallbackName = rawCompanyName.trim();
		return fallbackName || null;
	}
};

const schema = z
	.object({
		email: z.string().max(255, 'E-mail inválido').email('E-mail inválido'),
		password: z
			.string()
			.min(6, 'A senha deve ter no mínimo 6 caracteres')
			.max(255, 'A senha deve ter no máximo 255 caracteres')
			.regex(
				/^[0-9A-Za-zÀ-ÖØ-öø-ÿ!@#$%^&*()_+\-= [\]{};':"\\|,.<>/?~`]+$/,
				'A senha contém caracteres inválidos',
			)
			.regex(
				/^(?=.*[A-Za-z])(?=.*\d).+$/,
				'A senha deve conter pelo menos uma letra e um número',
			),
		repeat_password: z.string(),
		first_name: z
			.string()
			.regex(
				/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
				'Nome inválido, apenas letras são permitidas',
			)
			.transform(normalizeName)
			.refine((val: string) => val.length >= 3, 'Mínimo 3 caracteres'),
		last_name: z
			.string()
			.regex(
				/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
				'Sobrenome inválido, apenas letras são permitidas',
			)
			.transform(normalizeName)
			.refine((val: string) => val.length >= 3, 'Mínimo 3 caracteres'),
		birthday: z
			.string()
			.regex(
				/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19\d{2}|20[01]\d|202[0-9])$/,
				'Data inválida (dd/mm/aaaa)',
			)
			.transform(transformBirthday),
		phone_number: z
			.string()
			.min(1, 'Telefone é obrigatório')
			.transform(removeMask)
			.refine(
				(val) => val.length === 11,
				'Número de telefone deve ter 11 dígitos',
			)
			.refine(
				(val) => /^\d+$/.test(val),
				'Número de telefone deve conter apenas números',
			),
		cpf: z
			.string()
			.min(1, 'CPF é obrigatório')
			.transform(removeMask)
			.refine((val) => val.length === 11, 'CPF deve ter 11 dígitos')
			.refine(
				(val) => /^\d+$/.test(val),
				'CPF deve conter apenas números',
			),
		terms_accepted: z.literal(true, {
			errorMap: () => ({ message: 'Você deve aceitar os termos de uso' }),
		}),
	})
	.refine((data) => data.password === data.repeat_password, {
		message: 'As senhas não coincidem',
		path: ['repeat_password'],
	});

const CollaboratorOnboarding = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { mutateAsync: createUserMutateAsync } = useCreateUser();
	const { openModal } = useModal();
	const { resumeSchedulingPayAfterAuth } = useResumeSchedulingPayAfterAuth();

	type FormData = z.infer<typeof schema>;

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	const companyName = useMemo(
		() => formatCompanyName(searchParams.get('empresa')),
		[searchParams],
	);

	const onSubmit = async (data: FormData) => {
		const { terms_accepted, repeat_password, ...payload } = data;

		try {
			const res = await createUserMutateAsync(payload);

			if (!res.success) return;

			toast.success('Cadastro realizado com sucesso!');
			setUserData(res.user);

			const b2bFlow = checkBenefitRedirection();
			if (b2bFlow.shouldRedirect && b2bFlow.path) {
				clearPendingBenefitData();
				markUserFirstAccess();
				navigate(b2bFlow.path, { state: b2bFlow.state });
				return;
			}

			const isSchedulingContinuation =
				localStorage.getItem('isSchedulingFlow') === 'true' &&
				localStorage.getItem(SCHEDULING_REGISTRATION_PENDING_KEY) ===
					'true';
			if (isSchedulingContinuation) {
				const sessionDataString = localStorage.getItem('session_data');
				if (!sessionDataString) return;

				const completeSessionData = JSON.parse(
					sessionDataString,
				) as Record<string, unknown> & { user_id?: string };
				const { user_id: _ignoredUserId, ...body } =
					completeSessionData;
				const schedulingBody = body as unknown as Sessions.Create;

				if (
					!schedulingSelectedDateStillBookable(
						schedulingBody.selected_date,
					)
				) {
					clearSchedulingPayDraftSilently();
					markUserFirstAccess();
					navigate('/agendamento', { replace: true });
					return;
				}

				localStorage.removeItem(SCHEDULING_REGISTRATION_PENDING_KEY);
				markUserFirstAccess();
				void resumeSchedulingPayAfterAuth(navigate);
				return;
			}

			markUserFirstAccess();
			navigate('/primeiro-acesso');
		} catch (_error) {
			toast.error('Erro ao finalizar cadastro. Verifique os dados.');
		}
	};

	const iconCommonClass = 'h-4 w-4 text-stone-400';

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Onboarding de Colaborador | Plumaa</title>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-2 md:p-4">
				<div className="flex w-full max-w-[880px] flex-col gap-6 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					<div className="space-y-1 text-center">
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400 md:text-xs">
							onboarding de colaborador
						</p>
						<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
							{companyName
								? `Olá, colaborador da empresa ${companyName}`
								: 'Olá, colaborador'}
						</h1>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							Complete seus dados para criar seu acesso com a
							empresa correta e responda ao formulário.
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-6"
					>
						<section className="space-y-3">
							<div>
								<h2 className="text-sm font-black uppercase tracking-widest text-stone-700">
									Dados obrigatórios
								</h2>
							</div>

							<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
								<label className="space-y-1">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										Nome *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<UserRound
												className={iconCommonClass}
											/>
										}
										error={errors.first_name?.message}
										value={watch('first_name')}
										displayError
									>
										<Input
											{...register('first_name')}
											value={watch('first_name') || ''}
											onChange={(e) =>
												setValue(
													'first_name',
													e.target.value.replace(
														/\s+/g,
														' ',
													),
													{
														shouldValidate: false,
													},
												)
											}
											placeholder="Seu nome"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>

								<label className="space-y-1">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										Sobrenome *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<UserRound
												className={iconCommonClass}
											/>
										}
										error={errors.last_name?.message}
										value={watch('last_name')}
										displayError
									>
										<Input
											{...register('last_name')}
											value={watch('last_name') || ''}
											onChange={(e) =>
												setValue(
													'last_name',
													e.target.value.replace(
														/\s+/g,
														' ',
													),
													{
														shouldValidate: false,
													},
												)
											}
											placeholder="Seu sobrenome"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>

								<label className="space-y-1 md:col-span-2">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										E-mail *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<Mail className={iconCommonClass} />
										}
										error={errors.email?.message}
										value={watch('email')}
										displayError
									>
										<Input
											{...register('email')}
											type="email"
											placeholder="nome@exemplo.com"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>

								<label className="space-y-1">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										Telefone *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<Phone
												className={iconCommonClass}
											/>
										}
										error={errors.phone_number?.message}
										value={watch('phone_number')}
										displayError
									>
										<Input
											{...register('phone_number')}
											maxLength={15}
											value={maskPhone(
												watch('phone_number') || '',
											)}
											onChange={(e) =>
												setValue(
													'phone_number',
													maskPhone(e.target.value),
													{
														shouldValidate: true,
													},
												)
											}
											placeholder="(11) 99999-9999"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>

								<label className="space-y-1">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										Data de nascimento *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<CalendarDays
												className={iconCommonClass}
											/>
										}
										error={errors.birthday?.message}
										value={watch('birthday')}
										displayError
									>
										<Input
											{...register('birthday')}
											type="text"
											maxLength={10}
											value={maskDate(
												watch('birthday') || '',
											)}
											onChange={(e) =>
												setValue(
													'birthday',
													maskDate(e.target.value),
													{
														shouldValidate: true,
													},
												)
											}
											placeholder="dd/mm/aaaa"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>

								<label className="space-y-1 md:col-span-2">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										CPF *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<IdCard
												className={iconCommonClass}
											/>
										}
										error={errors.cpf?.message}
										value={watch('cpf')}
										displayError
									>
										<Input
											{...register('cpf')}
											maxLength={14}
											value={maskCPF(watch('cpf') || '')}
											onChange={(e) =>
												setValue(
													'cpf',
													maskCPF(e.target.value),
													{
														shouldValidate: true,
													},
												)
											}
											placeholder="000.000.000-00"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>

								<label className="space-y-1 md:col-span-2">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										Senha *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<Lock className={iconCommonClass} />
										}
										error={errors.password?.message}
										value={watch('password')}
										displayError
									>
										<Input
											{...register('password')}
											type="password"
											placeholder="********"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>

								<label className="space-y-1 md:col-span-2">
									<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
										Confirmar senha *
									</span>
									<InputWrapperAuth
										className="max-w-none"
										icon={
											<Lock className={iconCommonClass} />
										}
										error={errors.repeat_password?.message}
										value={watch('repeat_password')}
										displayError
									>
										<Input
											{...register('repeat_password')}
											type="password"
											placeholder="********"
											className="h-9 border-stone-200 text-sm focus:border-stone-400"
										/>
									</InputWrapperAuth>
								</label>
							</div>
						</section>

						<div className="space-y-2 py-2">
							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									id="terms_accepted"
									className="h-4 w-4 cursor-pointer accent-stone-900 focus:outline-none"
									{...register('terms_accepted')}
								/>
								<label
									htmlFor="terms_accepted"
									className="flex-1 text-[10px] leading-snug text-stone-500 md:text-xs"
								>
									Confirmo que li e aceito os
									<button
										type="button"
										onClick={() =>
											openModal({
												title: 'Termos de Uso',
												content: (
													<CollaboratorTermsOfUsePage />
												),
											})
										}
										className="mx-1 font-black text-stone-900 hover:underline"
									>
										termos de uso
									</button>
									e
									<button
										type="button"
										onClick={() =>
											openModal({
												title: 'Política de Privacidade',
												content: <PrivacyPolicyPage />,
											})
										}
										className="mx-1 font-black text-stone-900 hover:underline"
									>
										política de privacidade
									</button>
								</label>
							</div>
							{errors.terms_accepted?.message && (
								<InputError
									error={errors.terms_accepted.message}
								/>
							)}
						</div>

						<Button
							type="submit"
							className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-stone-800 md:mx-auto md:w-auto md:min-w-[230px]"
							text="Finalizar cadastro"
							endIcon={
								<ArrowRightCircle size={14} className="ml-1" />
							}
						/>
					</form>
				</div>
			</main>
		</div>
	);
};

export { CollaboratorOnboarding };
